import { Link } from "react-router-dom";
import { ArrowRight, DollarSign, TrendingUp, Building2, CheckCircle2, Rocket, Wallet, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/shared/Navbar";
import { Reveal } from "../components/Reveal";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/ba4e6931-59f6-4a5b-81ee-19e152c56f21/images/3b9f318dd8f3dc2717df6938fcbdff2e6439339f6eaa00ffe0a816c70f178f04.jpeg";
const EARN_IMG = "https://static.prod-images.emergentagent.com/jobs/ba4e6931-59f6-4a5b-81ee-19e152c56f21/images/1192f87ec0267a5b40379a516eab33531986869bfbfbbf4f0d60883c34f16651.jpeg";

const HOOKS = [
  {
    icon: DollarSign,
    title: "Earn real money",
    text: "Set your own prices and get paid for every campaign you deliver. Cash out to PayPal — you keep the majority of every order.",
  },
  {
    icon: TrendingUp,
    title: "Help artists grow",
    text: "Turn your audience into real streams, views and fans. Do work you actually enjoy — promoting music you believe in.",
  },
  {
    icon: Building2,
    title: "Work with real labels",
    text: "Get discovered by verified artists and record labels running paid campaigns. Steady, serious clients — not one-off gigs.",
  },
];

const STEPS = [
  { n: "01", title: "Create your profile", text: "Sign up in seconds and show off your TikTok, Instagram, Spotify or YouTube reach." },
  { n: "02", title: "List your gigs", text: "Offer promotion packages with your price, delivery time and tags so buyers can find you." },
  { n: "03", title: "Get paid", text: "Accept orders, deliver results, and withdraw your earnings straight to PayPal." },
];

const PERKS = [
  "No upfront fees to join",
  "You set your own prices",
  "Fast PayPal withdrawals",
  "Verified artists & labels",
  "Build reviews & reputation",
  "Work whenever you want",
];

const BecomePromoter = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A] overflow-x-hidden" data-testid="become-promoter-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28" data-testid="bp-hero">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#CCFF00]/30 bg-[#CCFF00]/10 px-4 py-1.5 mb-8">
              <span className="w-2 h-2 rounded-full bg-[#CCFF00] animate-pulse" />
              <span className="text-xs uppercase tracking-[0.2em] text-[#CCFF00] font-bold">For creators & curators</span>
            </div>

            <h1 className="font-['Syne'] font-extrabold tracking-tighter leading-[0.95] text-white text-5xl sm:text-6xl lg:text-7xl">
              Get paid to promote
              <span className="block text-[#CCFF00]">the music you love</span>
            </h1>

            <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-xl">
              Turn your TikTok, Instagram, Spotify or YouTube audience into income.
              Join DOPE MEDIA as a promoter, earn real money, help artists grow, and
              work with verified labels.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register?role=promoter" data-testid="bp-hero-cta">
                <Button className="btn-primary rounded-full px-8 py-6 text-base group">
                  Become a promoter
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/marketplace" data-testid="bp-hero-secondary">
                <Button variant="outline" className="rounded-full px-8 py-6 text-base bg-transparent border-white/20 text-white hover:bg-white/5 hover:border-white/40">
                  See live gigs
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-zinc-500">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#CCFF00]" /> Free to join</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#CCFF00]" /> Set your own prices</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#CCFF00]" /> Paid via PayPal</span>
            </div>
          </div>
        </div>
      </section>

      {/* THREE HOOKS */}
      <section className="py-20 md:py-24 border-t border-white/5" data-testid="bp-hooks">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOOKS.map((h, i) => (
              <Reveal key={h.title} delay={i * 90}>
                <div className="h-full rounded-2xl border border-white/10 bg-[#111111] p-8 hover:border-[#CCFF00]/40 transition-colors">
                  <div className="grid place-items-center w-12 h-12 rounded-xl bg-[#CCFF00]/10 text-[#CCFF00] mb-6">
                    <h.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-['Syne'] text-2xl font-bold text-white mb-3">{h.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{h.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-24 bg-[#0C0C0C] border-t border-white/5" data-testid="bp-steps">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <h2 className="font-['Syne'] text-3xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
              How it works
            </h2>
            <p className="text-zinc-400 text-lg max-w-xl mb-14">Start earning in three simple steps.</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 100}>
                <div>
                  <div className="font-['Syne'] text-5xl font-extrabold text-[#CCFF00]/30 mb-4">{s.n}</div>
                  <h3 className="font-['Syne'] text-xl font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EARNINGS / PERKS */}
      <section className="py-20 md:py-24 border-t border-white/5" data-testid="bp-perks">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img src={EARN_IMG} alt="Earn money promoting music" className="w-full h-full object-cover" data-testid="bp-earn-img" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <h2 className="font-['Syne'] text-3xl md:text-5xl font-extrabold tracking-tight text-white mb-6">
                Your audience is worth money
              </h2>
              <p className="text-zinc-400 text-lg mb-8 max-w-lg">
                If you have followers, playlist power, or edit skills — artists will pay
                for that reach. Keep the majority of every order and cash out anytime.
              </p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PERKS.map((p, i) => (
                <Reveal key={p} delay={i * 60}>
                  <div className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle2 className="w-5 h-5 text-[#CCFF00] shrink-0" />
                    <span>{p}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-[#0C0C0C] border-t border-white/5" data-testid="bp-final-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex justify-center gap-6 mb-8 text-[#CCFF00]">
            <Rocket className="w-7 h-7" />
            <Wallet className="w-7 h-7" />
            <ShieldCheck className="w-7 h-7" />
          </div>
          <h2 className="font-['Syne'] text-4xl md:text-6xl font-extrabold tracking-tighter text-white mb-6">
            Start earning today
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-10">
            It's free to join. Create your promoter profile, list your first gig, and
            get discovered by artists and labels.
          </p>
          <Link to="/register?role=promoter" data-testid="bp-final-cta-btn">
            <Button className="btn-primary rounded-full px-10 py-7 text-lg group">
              Become a promoter
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 border-t border-[#2A2A2A]" data-testid="bp-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="font-['Syne'] text-xl font-extrabold tracking-tighter text-white">
            DOPE<span className="text-[#CCFF00]">.</span>MEDIA
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-zinc-500">
            <Link to="/marketplace" className="hover:text-white transition-colors">Marketplace</Link>
            <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <a href="mailto:dopemediallc@gmail.com" className="hover:text-white transition-colors">Contact</a>
          </div>
          <div className="text-sm text-zinc-600">© 2026 DOPE MEDIA</div>
        </div>
      </footer>
    </div>
  );
};

export default BecomePromoter;
